"use client";

import { TableOfContents } from "@/features/marketing/components/blog/TableOfContents";
import { DynamicCaseStudies } from "@/components/shared/dynamic_case_studies";
import { QuoteCarousel } from "@/components/shared/quote_carousel";

interface BlogSection {
    heading: string;
    content: string[];
}

interface OurStoryBlogProps {
    title: string;
    contentSections: BlogSection[];
    recommendedItems?: Array<{
        title: string;
        text: string;
        imageUrl: string;
        link: string;
    }>;
    quotes?: Array<{
        text: string;
        author: string;
        title: string;
        image: string;
    }>;
}

export function OurStoryBlog({ title, contentSections, recommendedItems = [], quotes = [] }: OurStoryBlogProps) {
    // Transform content sections to match the TableOfContents expected format
    const blogSubject = {
        contentSections: contentSections.map((section, index) => ({
            heading: section.heading,
            content: section.content,
            id: `section-${index}`
        }))
    };

    return (
        <section className="py-16 lg:py-24" role="region" aria-label="Our Story Blog">
            <div className="flex min-h-fit w-full justify-center pb-24">
                <div className="flex w-full px-5 md:px-0 max-w-7xl gap-0 md:w-10/12 md:gap-8 lg:w-10/12 lg:gap-12 xl:gap-24">
                    {/* Table of Contents - Hidden on mobile, visible on lg and up */}
                    <aside className="hidden lg:block shrink-0 mt-16 lg:w-64 xl:w-80">
                        <div className="sticky top-8">
                            <TableOfContents subject={blogSubject} /> 
                        </div>
                    </aside>
                    
                    {/* Main content */}
                    <div className="flex-1 min-w-0">
                        <article className="prose prose-lg max-w-none md:max-w-3xl lg:max-w-4xl rounded-2xl bg-white px-6 pb-6 lg:px-8 lg:pb-8 mt-16" role="article" aria-label={title}>
                            <h2 className="mb-8 text-2xl font-bold tracking-tight text-gray-900 md:text-3xl">
                                {title}
                            </h2>

                            <div className="space-y-8">
                                {contentSections.map((section, index) => (
                                    <div
                                        key={index}
                                        id={`section-${index}`}
                                        className="scroll-mt-24"
                                    >
                                        <h3 className="mb-4 text-xl font-semibold text-gray-900">
                                            {section.heading}
                                        </h3>
                                        <div className="space-y-4">
                                            {section.content.map((paragraph, pIndex) => (
                                                <p key={pIndex} className="text-gray-600 leading-relaxed">
                                                    {paragraph.split(/(\*\*.*?\*\*|\*.*?\*)/).map((part, partIndex) => {
                                                        if (part.startsWith('**') && part.endsWith('**')) {
                                                            return <strong key={partIndex} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
                                                        } else if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
                                                            return <em key={partIndex} className="italic">{part.slice(1, -1)}</em>;
                                                        }
                                                        return part;
                                                    })}
                                                </p>
                                            ))}
                                        </div>
                                        
                                        {/* Add quote carousel after the first section */}
                                        {index === 0 && quotes.length > 0 && (
                                            <div className="my-12">
                                                <QuoteCarousel 
                                                    quotes={quotes} 
                                                    variant="blog-quote" 
                                                    className="mx-auto"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </article>
                    </div>
                </div>
            </div>

            {/* Recommended readings section */}
            {recommendedItems.length > 0 && (
                <section className="relative overflow-hidden bg-linear-to-br from-indigo-50/40 via-white to-gray-50/30" role="region" aria-label="Recommended reading">
                    <div className="absolute inset-0 bg-size-[24px_24px] [background:linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)]"></div>
                    <div className="relative max-w-7xl mx-auto px-6 py-16">
                        <h2 className="text-3xl font-bold text-gray-900 md:text-4xl mb-8">
                            Keep Reading
                        </h2>
                        <DynamicCaseStudies 
                            items={recommendedItems} 
                            showViewAll={false}
                            showKeepReading={false}
                            className="pt-0"
                        />
                    </div>
                </section>
            )}
        </section>
    );
}
